import React, { useEffect, useMemo, useState } from "react";
import { AccountLink } from "../components/resellers/AccountLink";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { fmtDate, fmtNum } from "../lib/format";
import { Plus, Trash2, Search, X, Eye, ClipboardList } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent } from "../components/ui/card";
import { useConfirm } from "../components/ConfirmDialog";
import PackInsertDialog from "../components/packs/PackInsertDialog";
import { toast } from "sonner";

// Phase 101du.9 — icône d'ordre définie hors composant (react/no-unstable-nested-components)
const SortIcon = ({ activeKey, activeDir, column }) => {
  if (activeKey !== column) return <span className="ml-1 text-[10px] opacity-30">↕</span>;
  return <span className="ml-1 text-[10px] text-[#1F4E3D] font-bold">{activeDir === "asc" ? "↑" : "↓"}</span>;
};

const STATUS_META = {
  NOUVELLE: { label: "Projet", className: "bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D3A8]" },
  VALIDEE: { label: "Validée", className: "bg-[#E5EFF8] text-[#2D5A87] border-[#C5D8E8]" },
  EN_PRODUCTION: { label: "En production", className: "bg-[#E5EFF8] text-[#2D5A87] border-[#C5D8E8]" },
  PRETE: { label: "Prête", className: "bg-[#EFEAFA] text-[#5847A6] border-[#D4C8F0]" },
  LIVREE: { label: "Livrée", className: "bg-[#EAF4EE] text-[#1F4E3D] border-[#C5DBCF]" },
  FACTUREE: { label: "Facturée", className: "bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D3A8]" },
  PAYEE: { label: "Payée", className: "bg-[#EAF4EE] text-[#1F4E3D] border-[#C5DBCF]" },
  ANNULEE: { label: "Annulée", className: "bg-[#FCEEEA] text-[#C05C47] border-[#E8C1B4]" },
};

export default function ClientOrders() {
  const confirm = useConfirm();
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [open, setOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterDist, setFilterDist] = useState("all");
  const [search, setSearch] = useState("");
  // Phase 101du.9 — Tri par en-tête cliquable (défaut : commande la plus récente d'abord)
  const [sortKey, setSortKey] = useState("order_date");
  const [sortDir, setSortDir] = useState("desc"); // "asc" | "desc"
  // Phase 101bu — cache des lots dispo par produit (Conthey uniquement, tri DLC la plus courte en premier).
  // Alimenté à la sélection d'un produit dans une ligne de commande.
  const [lotsByProduct, setLotsByProduct] = useState({});

  const emptyForm = { distributor_id: "", order_date: "", delivery_date_target: "", notes: "", lines: [], sale_type: "fabrication" };
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    const [o, p, d, s] = await Promise.all([
      api.get("/client-orders"),
      api.get("/products"),
      api.get("/distributors"),
      api.get("/settings"),
    ]);
    setOrders(o.data);
    setProducts(p.data);
    setDistributors(d.data);
    // Pre-fill VAT from settings
    setForm((f) => ({ ...f, _default_vat: s.data.default_vat_rate || 2.6 }));
  };
  useEffect(() => { load(); }, []);

  const productMap = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);
  const distMap = useMemo(() => Object.fromEntries(distributors.map((d) => [d.id, d])), [distributors]);
  const clients = useMemo(() => distributors.filter((d) => d.type === "CLIENT"), [distributors]);

  const addLine = () => setForm({ ...form, lines: [...form.lines, { product_id: "", quantity: 1, unit_price: "", vat_rate: form._default_vat ?? 2.6, notes: "", preselected_lot_id: "" }] });
  // Phase 101bu — fetch lots dispo pour un produit (Conthey), triés DLC ASC (les plus courtes en premier).
  // Idempotent : ne refetch pas si déjà en cache.
  const fetchLotsForProduct = async (pid) => {
    if (!pid || lotsByProduct[pid]) return;
    try {
      // Phase 101bw — Pour la revente : inclure les lots dédiés (stock physique fongible).
      const isResale = form.sale_type === "resale";
      const q = isResale ? "?include_dedicated=true" : "";
      const r = await api.get(`/moma-refills/available-lots/${pid}${q}`);
      // Tri DLC DESC — DLC la plus LONGUE en premier
      const sortedLots = (r.data?.lots || []).slice().sort((a, b) => {
        const da = a.dlc || "0000";
        const db = b.dlc || "0000";
        return db.localeCompare(da);
      });
      setLotsByProduct((prev) => ({ ...prev, [pid]: sortedLots }));
    } catch {
      setLotsByProduct((prev) => ({ ...prev, [pid]: [] }));
    }
  };
  const updateLine = (idx, patch) => {
    // Si le produit change, on reset le lot pré-sélectionné et on charge les lots dispos.
    if (patch.product_id !== undefined) {
      patch = { ...patch, preselected_lot_id: "" };
      fetchLotsForProduct(patch.product_id);
    }
    setForm((f) => ({ ...f, lines: f.lines.map((ln, i) => i === idx ? { ...ln, ...patch } : ln) }));
  };
  const removeLine = (idx) => setForm({ ...form, lines: form.lines.filter((_, i) => i !== idx) });
  const insertPackLines = (packLines) => {
    setForm((f) => ({
      ...f,
      lines: [
        ...f.lines,
        ...packLines.map((ln) => ({
          product_id: ln.product_id,
          quantity: ln.quantity,
          unit_price: ln.unit_price,
          vat_rate: ln.vat_rate,
          notes: ln.notes || "",
          pack_id: ln.pack_id,
          pack_label: ln.pack_label,
        })),
      ],
    }));
  };

  const save = async () => {
    if (!form.distributor_id) { toast.error("Sélectionnez un client"); return; }
    if (form.lines.length === 0) { toast.error("Ajoutez au moins une ligne"); return; }
    for (const ln of form.lines) {
      if (!ln.product_id) { toast.error("Toutes les lignes doivent avoir un produit"); return; }
      if (!ln.quantity || Number(ln.quantity) <= 0) { toast.error("Quantité invalide"); return; }
    }
    try {
      await api.post("/client-orders", {
        distributor_id: form.distributor_id,
        order_date: form.order_date ? new Date(form.order_date).toISOString() : null,
        delivery_date_target: form.delivery_date_target || null,
        notes: form.notes,
        sale_type: form.sale_type,
        lines: form.lines.map((ln) => ({
          product_id: ln.product_id,
          quantity: Number(ln.quantity),
          unit_price: ln.unit_price ? Number(ln.unit_price) : null,
          vat_rate: ln.vat_rate !== undefined ? Number(ln.vat_rate) : 2.6,
          notes: ln.notes || "",
          pack_id: ln.pack_id || null,
          pack_label: ln.pack_label || null,
          preselected_lot_id: ln.preselected_lot_id || null,
        })),
      });
      toast.success("Commande créée");
      setOpen(false);
      setForm(emptyForm);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (co) => {
    const isCancelled = co.status === "ANNULEE";
    const hasLinked = (co.of_ids || []).length > 0 || co.bl_id;
    const ok = await confirm({
      title: "Supprimer cette commande ?",
      description: isCancelled && hasLinked
        ? `La commande ${co.reference} et ses entités liées annulées (OFs et BL) seront définitivement purgées de la base. Cette action est irréversible.`
        : `La commande ${co.reference} sera supprimée. Cette action est irréversible.`,
      destructive: true,
      confirmText: "Supprimer",
    });
    if (!ok) return;
    try {
      await api.delete(`/client-orders/${co.id}`);
      toast.success(isCancelled && hasLinked ? "Commande et entités liées supprimées" : "Commande supprimée");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const filtered = orders.filter((o) => {
    if (filterStatus !== "all" && o.status !== filterStatus) return false;
    if (filterDist !== "all" && o.distributor_id !== filterDist) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (o.reference || "").toLowerCase().includes(q) || (o.distributor_name || "").toLowerCase().includes(q);
  });

  // Phase 101du.9 — Tri après filtrage sur key + direction
  const sorted = useMemo(() => {
    const arr = [...filtered];
    const getVal = (o) => {
      if (sortKey === "reference") return o.reference || "";
      if (sortKey === "order_date") return o.order_date || o.created_at || "";
      if (sortKey === "distributor_name") return (o.distributor_name || "").toLowerCase();
      if (sortKey === "total_ht") return (o.lines || []).reduce((a, ln) => a + (ln.quantity || 0) * (ln.unit_price || 0), 0);
      if (sortKey === "status") return o.status || "";
      return "";
    };
    arr.sort((a, b) => {
      const va = getVal(a);
      const vb = getVal(b);
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (key === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir(key === "reference" || key === "distributor_name" || key === "status" ? "asc" : "desc"); }
  };

  // KPI
  const today = new Date().toISOString().slice(0, 10);
  const kpiActive = orders.filter((o) => !["PAYEE", "ANNULEE", "LIVREE"].includes(o.status)).length;
  const kpiThisMonth = orders.filter((o) => (o.created_at || "").slice(0, 7) === today.slice(0, 7)).length;
  const kpiCAMonth = orders
    .filter((o) => (o.created_at || "").slice(0, 7) === today.slice(0, 7))
    .reduce((acc, o) => acc + (o.lines || []).reduce((a, ln) => a + (ln.quantity || 0) * (ln.unit_price || 0), 0), 0);

  return (
    <div className="space-y-6" data-testid="client-orders-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-heading text-[#1A1C1A]">Commandes clients</h1>
          <p className="text-sm text-[#575A57]">Workflow Commande → OF → BL → Facture pour vos clients suisses</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setForm(emptyForm); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="create-co-btn">
              <Plus className="w-4 h-4 mr-2" /> Nouvelle commande
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">Nouvelle commande client</DialogTitle>
              <DialogDescription>Une référence CMD-AAMMJJ-NNN sera générée. Vous pourrez ensuite générer l&apos;OF de production puis le BL et la facture.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-12 gap-3">
                <div className="col-span-12 md:col-span-5">
                  <Label className="mb-1.5 block">Client *</Label>
                  <Select value={form.distributor_id} onValueChange={(v) => {
                    const chosen = clients.find(d => d.id === v);
                    const nextType = chosen?.default_sale_type || "fabrication";
                    setForm({ ...form, distributor_id: v, sale_type: nextType });
                  }}>
                    <SelectTrigger data-testid="co-distributor"><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
                    <SelectContent>
                      {clients.length === 0 ? (
                        <div className="px-2 py-3 text-xs text-[#575A57]">Aucun client. Créez-en un dans Distributeurs.</div>
                      ) : clients.map((d) => (
                        <SelectItem key={d.id} value={d.id}>
                          {d.name}{d.code ? ` (${d.code})` : ""}
                          {d.default_sale_type === "resale" ? " · revente" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-6 md:col-span-3">
                  <Label className="mb-1.5 block flex items-center gap-1">
                    Date commande
                    <span className="text-[10px] text-[#A3A6A3] font-normal">(reçue par tél/mail)</span>
                  </Label>
                  <Input
                    type="date"
                    value={form.order_date}
                    onChange={(e) => setForm({ ...form, order_date: e.target.value })}
                    data-testid="co-order-date"
                    title="Date à laquelle le client vous a réellement passé sa commande (peut différer de la date de saisie)"
                  />
                </div>
                <div className="col-span-6 md:col-span-4">
                  <Label className="mb-1.5 block">Date de livraison souhaitée</Label>
                  <Input type="datetime-local" value={form.delivery_date_target} onChange={(e) => setForm({ ...form, delivery_date_target: e.target.value })} data-testid="co-date" />
                </div>
              </div>
              <div>
                <Label className="mb-1.5 block">Notes</Label>
                <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Instructions spéciales…" data-testid="co-notes" />
              </div>
              {/* Phase 101bn — Toggle type de vente : fabrication vs revente stock */}
              <div className="border border-[#E5E5E0] rounded-md p-3 bg-[#FAFAF7]">
                <Label className="mb-2 block text-sm font-medium">Type de vente</Label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, sale_type: "fabrication" })}
                    className={`flex-1 px-3 py-2 rounded text-sm text-left border transition-all ${
                      form.sale_type === "fabrication"
                        ? "border-[#1F4E3D] bg-[#E8F2EC] text-[#1F4E3D] font-medium"
                        : "border-[#E5E5E0] bg-white text-[#575A57] hover:border-[#A3A6A3]"
                    }`}
                    data-testid="co-sale-type-fabrication"
                  >
                    <div className="font-medium">🍳 Fabrication</div>
                    <div className="text-[11px] mt-0.5 opacity-80">
                      Génère un OF → attend production → BL → facture (workflow classique)
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, sale_type: "resale" })}
                    className={`flex-1 px-3 py-2 rounded text-sm text-left border transition-all ${
                      form.sale_type === "resale"
                        ? "border-[#8C6B2F] bg-[#FBF4E9] text-[#8C6B2F] font-medium"
                        : "border-[#E5E5E0] bg-white text-[#575A57] hover:border-[#A3A6A3]"
                    }`}
                    data-testid="co-sale-type-resale"
                  >
                    <div className="font-medium">🔄 Revente stock</div>
                    <div className="text-[11px] mt-0.5 opacity-80">
                      Pas d&apos;OF, choix du lot manuel par produit, BL direct.
                    </div>
                  </button>
                </div>
                {form.sale_type === "resale" && (
                  <div className="mt-2 text-[11px] text-[#8C6B2F] bg-[#FFF8EA] border border-[#E8D3A8] rounded p-2">
                    ℹ️ Vente directe de stock existant. Choisissez librement les lots (DLC la plus longue en premier). La facture ne contiendra pas les DLC.
                  </div>
                )}
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Lignes de commande</Label>
                  <div className="flex items-center gap-2">
                    <PackInsertDialog
                      onInsert={insertPackLines}
                      defaultVatRate={form._default_vat ?? 2.6}
                      testIdPrefix="co-pack-insert"
                    />
                    <Button variant="outline" size="sm" onClick={addLine} data-testid="co-add-line"><Plus className="w-3.5 h-3.5 mr-1" /> Ajouter</Button>
                  </div>
                </div>
                {form.lines.length === 0 ? (
                  <div className="text-xs text-[#A3A6A3] border border-dashed border-[#E5E5E0] rounded p-4 text-center">
                    Cliquez sur « Ajouter » pour insérer un produit.
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      {form.lines.map((ln, idx) => (
                        <div key={idx} className="grid grid-cols-12 gap-2 items-end p-3 border border-[#E5E5E0] rounded bg-[#FAFAF7]">
                          {ln.pack_label && (
                            <div className="col-span-12 -mb-1">
                              <Badge className="bg-[#EAF4EE] text-[#1F4E3D] border-[#C5DBCF] text-[10px]" data-testid={`co-line-pack-${idx}`}>
                                Pack · {ln.pack_label}
                              </Badge>
                            </div>
                          )}
                          <div className="col-span-5">
                            <Label className="text-[11px] mb-1 block">Produit *</Label>
                            <Select value={ln.product_id} onValueChange={(v) => updateLine(idx, { product_id: v })}>
                              <SelectTrigger className="h-8 text-xs" data-testid={`co-line-product-${idx}`}><SelectValue placeholder="Choisir…" /></SelectTrigger>
                              <SelectContent>
                                {[...products].sort((a, b) => (a.name || "").localeCompare(b.name || "", "fr", { sensitivity: "base" })).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="col-span-2">
                            <Label className="text-[11px] mb-1 block">Qté *</Label>
                            <Input type="number" step="1" min="1" value={ln.quantity} onChange={(e) => updateLine(idx, { quantity: e.target.value })} className="h-8 text-xs" data-testid={`co-line-qty-${idx}`} />
                          </div>
                          <div className="col-span-2">
                            <Label className="text-[11px] mb-1 block">PU CHF</Label>
                            <Input type="number" step="0.10" value={ln.unit_price} onChange={(e) => updateLine(idx, { unit_price: e.target.value })} className="h-8 text-xs" placeholder="—" data-testid={`co-line-price-${idx}`} />
                          </div>
                          <div className="col-span-2">
                            <Label className="text-[11px] mb-1 block">TVA %</Label>
                            <Input type="number" step="0.1" value={ln.vat_rate} onChange={(e) => updateLine(idx, { vat_rate: e.target.value })} className="h-8 text-xs" data-testid={`co-line-vat-${idx}`} />
                          </div>
                          <div className="col-span-1 flex justify-end">
                            <Button variant="ghost" size="icon" onClick={() => removeLine(idx)} className="h-8 w-8 text-[#C05C47]" data-testid={`co-line-remove-${idx}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                          {/* Phase 101bu — Sélecteur de lot OBLIGATOIRE (mode revente).
                              Plus d'auto LIFO/FIFO : l'utilisateur choisit manuellement le lot pour chaque ligne. */}
                          {form.sale_type === "resale" && ln.product_id && (
                            <div className="col-span-12">
                              <Label className="text-[11px] mb-1 block text-[#8C6B2F]">
                                Lot / DLC · <span className="text-[#C05C47]">obligatoire — choix libre</span>
                              </Label>
                              <Select
                                value={ln.preselected_lot_id || ""}
                                onValueChange={(v) => updateLine(idx, { preselected_lot_id: v })}
                              >
                                <SelectTrigger
                                  className={`h-8 text-xs bg-white ${!ln.preselected_lot_id ? "border-[#C05C47]" : ""}`}
                                  data-testid={`co-line-lot-${idx}`}
                                >
                                  <SelectValue placeholder="— Choisir un lot —" />
                                </SelectTrigger>
                                <SelectContent>
                                  {(lotsByProduct[ln.product_id] || []).map((l) => {
                                    const dlcTxt = l.dlc ? fmtDate(l.dlc) : "sans DLC";
                                    const daysTxt = l.days_to_dlc != null
                                      ? (l.days_to_dlc < 0 ? " ⛔ périmé"
                                        : l.days_to_dlc <= 3 ? ` ⚠ J-${l.days_to_dlc}`
                                          : ` (J-${l.days_to_dlc})`)
                                      : "";
                                    const insufficient = Number(l.quantity || 0) < Number(ln.quantity || 0);
                                    return (
                                      <SelectItem
                                        key={l.id}
                                        value={l.id}
                                        disabled={insufficient}
                                        data-testid={`co-line-lot-option-${idx}-${l.lot_number}`}
                                      >
                                        <span className="font-mono">{l.lot_number || "(sans n°)"}</span>
                                        <span className="ml-2">· DLC {dlcTxt}{daysTxt}</span>
                                        <span className="ml-2 text-[#849F90]">· {l.quantity} dispo{insufficient ? " (insuffisant)" : ""}</span>
                                      </SelectItem>
                                    );
                                  })}
                                  {(lotsByProduct[ln.product_id] || []).length === 0 && (
                                    <SelectItem value="__NONE__" disabled>
                                      <span className="italic text-[#A3A6A3]">Aucun lot dispo à Conthey</span>
                                    </SelectItem>
                                  )}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    {/* Phase 101bu — Badge « DLC alignée » quand tous les lots choisis partagent la même DLC */}
                    {form.sale_type === "resale" && (() => {
                      const productLines = form.lines.filter((l) => l.product_id);
                      if (productLines.length === 0) return null;
                      const missing = productLines.filter((l) => !l.preselected_lot_id);
                      if (missing.length > 0) {
                        return (
                          <div className="mt-2 flex items-center gap-2 px-3 py-1.5 rounded border bg-[#FCEEEA] border-[#F0C8BF] text-[#B93A25] text-xs" data-testid="co-lots-missing-badge">
                            <span>⚠</span>
                            {missing.length} ligne{missing.length > 1 ? "s" : ""} sans lot choisi — obligatoire avant génération du BL
                          </div>
                        );
                      }
                      const selectedLots = productLines
                        .map((ln) => (lotsByProduct[ln.product_id] || []).find((l) => l.id === ln.preselected_lot_id))
                        .filter(Boolean);
                      const dlcs = new Set(selectedLots.map((l) => l.dlc || ""));
                      if (dlcs.size === 1 && [...dlcs][0]) {
                        return (
                          <div className="mt-2 flex items-center gap-2 px-3 py-1.5 rounded border bg-[#EAF4EE] border-[#C5DBCF] text-[#1F4E3D] text-xs" data-testid="co-lots-aligned-badge">
                            <span className="text-lg">🎯</span>
                            DLC alignée pour tous les produits : <b>{fmtDate([...dlcs][0])}</b>
                          </div>
                        );
                      }
                      return (
                        <div className="mt-2 flex items-center gap-2 px-3 py-1.5 rounded border bg-[#FBF4E9] border-[#E8D3A8] text-[#8C6B2F] text-xs" data-testid="co-lots-mixed-badge">
                          <span>⚠</span>
                          {dlcs.size} DLC différentes sur cette commande — pensez à aligner si nécessaire
                        </div>
                      );
                    })()}
                    {(() => {
                      const totalPlats = form.lines.reduce((acc, ln) => acc + (Number(ln.quantity) || 0), 0);
                      const totalCHF = form.lines.reduce((acc, ln) => acc + (Number(ln.quantity) || 0) * (Number(ln.unit_price) || 0), 0);
                      const isHigh = totalPlats > 100;
                      return (
                        <div
                          className={`mt-3 flex items-center justify-between px-4 py-2.5 rounded border ${isHigh ? "bg-[#FBF4E9] border-[#E8D3A8] text-[#8C6B2F]" : "bg-[#EAF4EE] border-[#C5DBCF] text-[#1F4E3D]"}`}
                          data-testid="co-line-counter"
                        >
                          <div className="text-sm">
                            <span className="font-mono font-bold text-lg">{totalPlats}</span>
                            <span className="text-xs ml-1.5">plats sur {form.lines.length} ligne{form.lines.length > 1 ? "s" : ""}</span>
                            {isHigh && <span className="text-[10px] ml-2 italic">⚠ contrôle de plausibilité conseillé</span>}
                          </div>
                          {totalCHF > 0 && (
                            <div className="text-sm font-mono">{totalCHF.toFixed(2)} CHF HT</div>
                          )}
                        </div>
                      );
                    })()}
                  </>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button onClick={save} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="save-co-btn">Créer la commande</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-[#E5E5E0]"><CardContent className="p-4">
          <div className="text-xs uppercase tracking-wide text-[#A3A6A3]">Commandes actives</div>
          <div className="font-mono text-2xl text-[#1A1C1A] mt-1">{kpiActive}</div>
        </CardContent></Card>
        <Card className="border-[#E5E5E0]"><CardContent className="p-4">
          <div className="text-xs uppercase tracking-wide text-[#A3A6A3]">Ce mois</div>
          <div className="font-mono text-2xl text-[#1A1C1A] mt-1">{kpiThisMonth}</div>
        </CardContent></Card>
        <Card className="border-[#E5E5E0]"><CardContent className="p-4">
          <div className="text-xs uppercase tracking-wide text-[#A3A6A3]">CA estimé du mois</div>
          <div className="font-mono text-2xl text-[#1F4E3D] mt-1">{fmtNum(kpiCAMonth)} CHF</div>
        </CardContent></Card>
      </div>

      {/* Filtres */}
      <div className="flex items-center gap-3 flex-wrap">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[180px]" data-testid="co-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            {Object.entries(STATUS_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterDist} onValueChange={setFilterDist}>
          <SelectTrigger className="w-[240px]" data-testid="co-dist-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les clients</SelectItem>
            {clients.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-[#A3A6A3] absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <Input type="text" placeholder="Rechercher référence ou client…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8 pr-8" data-testid="co-search" />
          {search && <button type="button" onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#A3A6A3] hover:text-[#575A57]"><X className="w-4 h-4" /></button>}
        </div>
      </div>

      <Card className="border-[#E5E5E0]">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <button type="button" onClick={() => toggleSort("reference")} className="inline-flex items-center hover:text-[#1F4E3D]" data-testid="co-sort-reference">
                    Référence<SortIcon activeKey={sortKey} activeDir={sortDir} column="reference" />
                  </button>
                </TableHead>
                <TableHead>
                  <button type="button" onClick={() => toggleSort("order_date")} className="inline-flex items-center hover:text-[#1F4E3D]" data-testid="co-sort-order-date">
                    Commande<SortIcon activeKey={sortKey} activeDir={sortDir} column="order_date" />
                  </button>
                </TableHead>
                <TableHead>
                  <button type="button" onClick={() => toggleSort("distributor_name")} className="inline-flex items-center hover:text-[#1F4E3D]" data-testid="co-sort-client">
                    Client<SortIcon activeKey={sortKey} activeDir={sortDir} column="distributor_name" />
                  </button>
                </TableHead>
                <TableHead className="text-right">Lignes</TableHead>
                <TableHead className="text-right">
                  <button type="button" onClick={() => toggleSort("total_ht")} className="inline-flex items-center hover:text-[#1F4E3D]" data-testid="co-sort-total">
                    Total HT<SortIcon activeKey={sortKey} activeDir={sortDir} column="total_ht" />
                  </button>
                </TableHead>
                <TableHead>
                  <button type="button" onClick={() => toggleSort("status")} className="inline-flex items-center hover:text-[#1F4E3D]" data-testid="co-sort-status">
                    Statut<SortIcon activeKey={sortKey} activeDir={sortDir} column="status" />
                  </button>
                </TableHead>
                <TableHead>Workflow</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-12 text-[#A3A6A3]">
                  <ClipboardList className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  Aucune commande. Cliquez sur « Nouvelle commande ».
                </TableCell></TableRow>
              ) : sorted.map((o) => {
                const meta = STATUS_META[o.status] || STATUS_META.NOUVELLE;
                const totalHT = (o.lines || []).reduce((acc, ln) => acc + (ln.quantity || 0) * (ln.unit_price || 0), 0);
                return (
                  <TableRow key={o.id} data-testid={`co-row-${o.id}`}>
                    <TableCell className="font-mono text-sm">
                      <Link to={`/commandes-clients/${o.id}`} className="text-[#1F4E3D] hover:underline">{o.reference}</Link>
                    </TableCell>
                    <TableCell className="text-xs text-[#575A57]" data-testid={`co-row-dates-${o.id}`}>
                      <div className="font-medium text-[#1A1C1A]" title="Date à laquelle le client a passé sa commande">
                        {fmtDate(o.order_date || o.created_at)}
                      </div>
                      {o.delivery_date_target && (
                        <div className="text-[10px] text-[#8C6B2F]" title="Date de livraison souhaitée">
                          livrer le {fmtDate(o.delivery_date_target)}
                        </div>
                      )}
                    </TableCell>
                    <TableCell><AccountLink resellerId={o.reseller_id} distributorId={o.distributor_id} compact testid={`co-account-${o.id}`}>{o.distributor_name}</AccountLink></TableCell>
                    <TableCell className="text-right font-mono text-xs">{(o.lines || []).length}</TableCell>
                    <TableCell className="text-right font-mono">{fmtNum(totalHT)} CHF</TableCell>
                    <TableCell><Badge className={meta.className}>{meta.label}</Badge></TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-[10px]">
                        {o.bl_id && <Badge variant="outline" className="text-[10px]">BL</Badge>}
                        {o.invoice_id && <Badge variant="outline" className="text-[10px]">F</Badge>}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Link to={`/commandes-clients/${o.id}`}>
                        <Button variant="ghost" size="icon" data-testid={`view-co-${o.id}`}><Eye className="w-4 h-4" /></Button>
                      </Link>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => remove(o)}
                        disabled={o.status !== "ANNULEE" && (!!o.invoice_id || !!o.bl_id || (o.of_ids || []).length > 0)}
                        title={
                          o.invoice_id
                            ? "Facture liée — impossible de supprimer"
                            : o.status === "ANNULEE"
                              ? "Supprimer définitivement (purge cascade)"
                              : (o.bl_id || (o.of_ids || []).length > 0)
                                ? "Commande liée à un OF/BL — annulez-la d'abord"
                                : "Supprimer"
                        }
                        data-testid={`delete-co-${o.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-[#C05C47]" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
